import React, { Component } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';

import { getAllPools, copyDoctors } from '../../services/api/pool-manager';
import PoolDoctorsList from './PoolDoctorsList';
import ConfirmModal from './ConfirmModal';
import { RegularText, BoldText } from '../common/Text';
import LoadingComponent from '../common/LoadingComponent';

const Card = styled.div`
  display: flex;
  flex-direction: column;
  margin: 12px;
  padding: 22px;
  background: #ffffff;
  border: 1px solid #eceaf3;
  border-radius: 14px;
  box-shadow: 0 6px 20px rgba(75, 46, 131, 0.06);
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-end;
  flex-wrap: wrap;
  gap: 18px;
  margin-top: 16px;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
`;

const Label = styled(RegularText)`
  color: #7a7391;
  font-size: 0.8rem;
  margin-bottom: 6px;
`;

const Select = styled.select`
  font-family: BrandonText-Medium, Lato, sans-serif;
  padding: 11px 14px;
  border: 1px solid #ddd6ee;
  border-radius: 10px;
  font-size: 0.9rem;
  min-width: 18rem;
  background: #ffffff;
  outline: none;
  :focus {
    border-color: #6b4bd6;
  }
`;

const Arrow = styled.div`
  font-size: 1.4rem;
  color: #b0a7cf;
  padding-bottom: 8px;
`;

const Actions = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 20px;
`;

const Button = styled.button`
  font-family: BrandonText-Medium, Lato, sans-serif;
  font-size: 0.9rem;
  padding: 11px 22px;
  border: none;
  border-radius: 10px;
  color: white;
  background: ${(props) =>
    props.secondary
      ? 'linear-gradient(135deg, #3f8fd6, #2f6fb0)'
      : 'linear-gradient(135deg, #6b4bd6, #4b2e83)'};
  box-shadow: 0 6px 14px rgba(75, 46, 131, 0.22);
  cursor: pointer;
  transition: opacity 0.15s ease;
  :hover {
    opacity: 0.9;
  }
  :disabled {
    background: #c5c0d4;
    box-shadow: none;
    cursor: not-allowed;
  }
`;

const HelperText = styled(RegularText)`
  color: #8a84a0;
  margin-top: 10px;
`;

class CopyDoctorsPanel extends Component {
  constructor(props) {
    super(props);
    this.state = {
      pools: [],
      loadingPools: false,
      sourcePoolId: '',
      destPoolId: '',
      selectedDoctorIds: [],
      copying: false,
      pendingMode: null,
    };
  }

  componentDidMount() {
    this.loadPools();
  }

  loadPools = () => {
    this.setState({ loadingPools: true });
    getAllPools()
      .then((res) => {
        if (res && res.message === 'success') {
          this.setState({ pools: res.pools || [] });
        } else {
          throw new Error((res && res.errorMessage) || 'Failed to fetch pools');
        }
      })
      .catch((err) => {
        toast.error(err.message || 'Failed to fetch pools');
        this.setState({ pools: [] });
      })
      .finally(() => {
        this.setState({ loadingPools: false });
      });
  };

  onSourceChange = (e) => {
    this.setState({ sourcePoolId: e.target.value, selectedDoctorIds: [] });
  };

  onDestChange = (e) => {
    this.setState({ destPoolId: e.target.value });
  };

  onSelectionChange = (nextIds) => {
    this.setState({ selectedDoctorIds: nextIds });
  };

  validate = () => {
    const { sourcePoolId, destPoolId } = this.state;
    if (!sourcePoolId || !destPoolId) {
      toast.error('Select both a source and a destination pool.');
      return false;
    }
    if (`${sourcePoolId}` === `${destPoolId}`) {
      toast.error('Source and destination pools must be different.');
      return false;
    }
    return true;
  };

  runCopy = (doctorIds) => {
    if (!this.validate()) {
      return;
    }
    const { sourcePoolId, destPoolId } = this.state;
    const { onCopied } = this.props;
    this.setState({ copying: true });
    copyDoctors(sourcePoolId, destPoolId, doctorIds)
      .then((res) => {
        if (res && res.message === 'success') {
          const count =
            res.copied !== undefined ? res.copied : (doctorIds || []).length;
          toast.success(`Copied ${count} doctor(s) to the destination pool.`);
          if (onCopied) {
            onCopied(destPoolId);
          }
        } else {
          throw new Error((res && res.errorMessage) || 'Failed to copy doctors');
        }
      })
      .catch((err) => {
        toast.error(err.message || 'Failed to copy doctors');
      })
      .finally(() => {
        this.setState({ copying: false, pendingMode: null });
      });
  };

  onCopyAll = () => {
    if (!this.validate()) {
      return;
    }
    this.setState({ pendingMode: 'all' });
  };

  onCopySelected = () => {
    const { selectedDoctorIds } = this.state;
    if (!selectedDoctorIds.length) {
      toast.error('Select at least one doctor to copy.');
      return;
    }
    if (!this.validate()) {
      return;
    }
    this.setState({ pendingMode: 'selected' });
  };

  onConfirmCopy = () => {
    const { pendingMode, selectedDoctorIds } = this.state;
    this.runCopy(pendingMode === 'all' ? null : selectedDoctorIds);
  };

  render() {
    const {
      pools,
      loadingPools,
      sourcePoolId,
      destPoolId,
      selectedDoctorIds,
      copying,
      pendingMode,
    } = this.state;

    const sourcePool = pools.find((p) => `${p.poolId}` === `${sourcePoolId}`);
    const destPool = pools.find((p) => `${p.poolId}` === `${destPoolId}`);
    const srcName = sourcePool ? sourcePool.poolName : 'source pool';
    const dstName = destPool ? destPool.poolName : 'destination pool';
    const confirmMessage =
      pendingMode === 'all'
        ? `Copy ALL doctors from "${srcName}" into "${dstName}"?`
        : `Copy ${selectedDoctorIds.length} selected doctor(s) from "${srcName}" into "${dstName}"?`;

    return (
      <Card>
        <BoldText fontSize="1.2rem">Copy Doctors Between Pools</BoldText>
        <HelperText>
          Copy all doctors — or a selected few — from one pool to another.
        </HelperText>

        {loadingPools ? (
          <LoadingComponent />
        ) : (
          <React.Fragment>
            <Row>
              <Field>
                <Label>Source pool</Label>
                <Select value={sourcePoolId} onChange={this.onSourceChange}>
                  <option value="">Select source pool…</option>
                  {pools.map((p) => (
                    <option key={p.poolId} value={p.poolId}>
                      {p.poolName} ({p.poolId})
                    </option>
                  ))}
                </Select>
              </Field>
              <Arrow>→</Arrow>
              <Field>
                <Label>Destination pool</Label>
                <Select value={destPoolId} onChange={this.onDestChange}>
                  <option value="">Select destination pool…</option>
                  {pools.map((p) => (
                    <option key={p.poolId} value={p.poolId}>
                      {p.poolName} ({p.poolId})
                    </option>
                  ))}
                </Select>
              </Field>
            </Row>

            <Actions>
              <Button disabled={copying} onClick={this.onCopyAll}>
                {copying ? 'Copying…' : 'Copy all doctors'}
              </Button>
              <Button
                secondary
                disabled={copying || !selectedDoctorIds.length}
                onClick={this.onCopySelected}
              >
                {`Copy selected (${selectedDoctorIds.length})`}
              </Button>
            </Actions>

            {sourcePoolId ? (
              <PoolDoctorsList
                key={sourcePoolId}
                poolId={sourcePoolId}
                poolName={sourcePool ? sourcePool.poolName : 'Source pool'}
                selectable
                wide
                selectedDoctorIds={selectedDoctorIds}
                onSelectionChange={this.onSelectionChange}
              />
            ) : (
              <HelperText>
                Select a source pool to choose specific doctors to copy.
              </HelperText>
            )}
          </React.Fragment>
        )}

        <ConfirmModal
          isOpen={!!pendingMode}
          title="Confirm copy"
          loading={copying}
          confirmText="Copy"
          message={confirmMessage}
          onConfirm={this.onConfirmCopy}
          onCancel={() => this.setState({ pendingMode: null })}
        />
      </Card>
    );
  }
}

export default CopyDoctorsPanel;
